import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTrackedProducts,
  getTrackedProductsWithRetailer,
  removeTracker,
  removeTrackerWithRetailer,
  addTracker,
  addTrackerWithRetailer,
  manuallyUpdateTracker,
  manuallyUpdateTrackerWithRetailer,
  getTrackersByProduct,
  deleteTrackersByProduct,
  isProductTrackedInStore,
  getTrackerByUpcAndStore,
  removeTrackerByUpcAndStore,
  // type TrackedProduct,
  type AddTrackerRequest,
} from "../services/TrackingService";
import { toast } from "react-toastify";

// Query keys
export const trackingKeys = {
  all: ["tracking"] as const,
  lists: () => [...trackingKeys.all, "list"] as const,
  list: () => [...trackingKeys.lists()] as const,
  listWithRetailer: (retailer: string) =>
    [...trackingKeys.lists(), { retailer }] as const,
  check: (upc: string) => [...trackingKeys.all, "check", upc] as const,
  checkWithRetailer: (upc: string, retailer: string) =>
    [...trackingKeys.all, "check", upc, retailer] as const,
};

/**
 * Hook for fetching tracked products (legacy - defaults to walmart)
 */
export const useTrackedProducts = () => {
  return useQuery({
    queryKey: trackingKeys.list(),
    queryFn: getTrackedProducts,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - all components share this
  });
};

/**
 * Hook for fetching tracked products with retailer support
 */
export const useTrackedProductsWithRetailer = (retailer: string) => {
  return useQuery({
    queryKey: trackingKeys.listWithRetailer(retailer),
    queryFn: () => getTrackedProductsWithRetailer(retailer),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - all components share this
    refetchOnMount: "always",
  });
};

/**
 * Hook for adding a tracker (legacy - defaults to walmart)
 */
export const useAddTracker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addTracker,
    onSuccess: () => {
      // Invalidate and refetch tracked products
      queryClient.invalidateQueries({ queryKey: trackingKeys.lists() });
      toast.success("Product added to tracking!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add tracker");
    },
  });
};

/**
 * Hook for adding a tracker with retailer support
 */
export const useAddTrackerWithRetailer = (retailer: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trackerData: AddTrackerRequest) =>
      addTrackerWithRetailer(trackerData, retailer),
    onSuccess: () => {
      // Invalidate and refetch tracked products for this retailer
      queryClient.invalidateQueries({
        queryKey: trackingKeys.listWithRetailer(retailer),
      });
      toast.success("Product added to tracking!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add tracker");
    },
  });
};

/**
 * Hook for removing a tracker (legacy - defaults to walmart)
 */
export const useRemoveTracker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeTracker,
    onSuccess: () => {
      // Invalidate and refetch tracked products
      queryClient.invalidateQueries({ queryKey: trackingKeys.lists() });
      toast.success("Product removed from tracking");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to remove tracker");
    },
  });
};

/**
 * Hook for removing a tracker with retailer support
 */
export const useRemoveTrackerWithRetailer = (retailer: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (upc: string) => removeTrackerWithRetailer(upc, retailer),
    onSuccess: () => {
      // Invalidate and refetch tracked products for this retailer
      queryClient.invalidateQueries({
        queryKey: trackingKeys.listWithRetailer(retailer),
      });
      toast.success("Product removed from tracking");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to remove tracker");
    },
  });
};

/**
 * Hook for manually updating a tracker (legacy - defaults to walmart)
 */
export const useManuallyUpdateTracker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: manuallyUpdateTracker,
    onSuccess: () => {
      // Invalidate and refetch tracked products
      queryClient.invalidateQueries({ queryKey: trackingKeys.lists() });
      toast.success("Tracker updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update tracker");
    },
  });
};

/**
 * Hook for manually updating a tracker with retailer support
 */
export const useManuallyUpdateTrackerWithRetailer = (retailer: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (upc: string) =>
      manuallyUpdateTrackerWithRetailer(upc, retailer),
    onSuccess: () => {
      // Invalidate and refetch tracked products for this retailer
      queryClient.invalidateQueries({
        queryKey: trackingKeys.listWithRetailer(retailer),
      });
      toast.success("Tracker updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update tracker");
    },
  });
};

/**
 * Hook for fetching all trackers for a specific product across all stores
 */
export const useTrackersByProduct = (actualUpc: string, retailer: string) => {
  return useQuery({
    queryKey: [...trackingKeys.all, "by-product", actualUpc, retailer],
    queryFn: () => getTrackersByProduct(actualUpc, retailer),
    staleTime: 0, // No caching - always fetch fresh data
    enabled: !!actualUpc, // Only run if actualUpc is provided
  });
};

/**
 * Hook for deleting all trackers for a specific product across all stores
 */
export const useDeleteTrackersByProduct = (retailer: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (actualUpc: string) =>
      deleteTrackersByProduct(actualUpc, retailer),
    onSuccess: (data) => {
      // Invalidate and refetch tracked products for this retailer
      queryClient.invalidateQueries({
        queryKey: trackingKeys.listWithRetailer(retailer),
      });
      toast.success(
        `Removed tracking for ${data.deleted} store${
          data.deleted > 1 ? "s" : ""
        }`
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to remove trackers");
    },
  });
};

/**
 * Hook for checking if a specific UPC+Store combination is being tracked
 */
export const useIsProductTrackedInStore = (
  upc: string | undefined,
  storeId: string | undefined,
  retailer: string
) => {
  return useQuery({
    queryKey: [...trackingKeys.all, "check-store", upc, storeId, retailer],
    queryFn: () => isProductTrackedInStore(upc!, storeId!, retailer),
    enabled: !!upc && !!storeId, // Only run if both UPC and storeId are provided
    staleTime: 0, // No caching - always fetch fresh data
  });
};

/**
 * Hook for getting a tracker by UPC and store
 */
export const useTrackerByUpcAndStore = (
  upc: string | undefined,
  storeId: string | undefined,
  retailer: string
) => {
  return useQuery({
    queryKey: [...trackingKeys.all, "by-upc-store", upc, storeId, retailer],
    queryFn: () => getTrackerByUpcAndStore(upc!, storeId!, retailer),
    enabled: !!upc && !!storeId, // Only run if both UPC and storeId are provided
    staleTime: 0, // No caching - always fetch fresh data
  });
};

/**
 * Hook for removing a tracker by UPC and store
 */
export const useRemoveTrackerByUpcAndStore = (retailer: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ upc, storeId }: { upc: string; storeId: string }) =>
      removeTrackerByUpcAndStore(upc, storeId, retailer),
    onSuccess: () => {
      // Invalidate and refetch tracked products for this retailer
      queryClient.invalidateQueries({
        queryKey: trackingKeys.listWithRetailer(retailer),
      });
      toast.success("Product removed from tracking");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to remove tracker");
    },
  });
};
